import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Users, CalendarDays, FileText, Receipt, BadgeCheck, CheckCircle2, AlertTriangle, XCircle, FileWarning } from "lucide-react";
import PageHeader from "@/components/PageHeader";
import { Link } from "react-router-dom";

const Stat = ({ icon: Icon, label, value, color, to, testId }) => {
  const colors = {
    blue: "bg-[#0066FF]/10 text-[#0066FF]",
    amber: "bg-amber-100 text-amber-700",
    emerald: "bg-emerald-100 text-emerald-700",
    slate: "bg-slate-100 text-slate-700",
  };
  const Wrapper = to ? Link : "div";
  return (
    <Wrapper to={to} className={to ? "block" : ""} data-testid={testId}>
      <Card className="p-4 sm:p-6 border-slate-200 hover:shadow-md transition-shadow">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <div className="text-[10px] sm:text-xs font-semibold uppercase tracking-wider text-slate-500 truncate">{label}</div>
            <div className="mt-1 sm:mt-2 font-heading text-2xl sm:text-3xl font-bold text-slate-900">{value}</div>
          </div>
          <div className={`h-9 w-9 sm:h-10 sm:w-10 rounded-lg flex items-center justify-center shrink-0 ${colors[color]}`}>
            <Icon className="h-4 w-4 sm:h-5 sm:w-5" />
          </div>
        </div>
      </Card>
    </Wrapper>
  );
};

const STATUS_PILL = {
  green:   { cls: "bg-emerald-100 text-emerald-700 border-emerald-200", Icon: CheckCircle2, label: "Valide" },
  orange:  { cls: "bg-amber-100 text-amber-700 border-amber-200",       Icon: AlertTriangle, label: "Bientôt expirée" },
  red:     { cls: "bg-rose-100 text-rose-700 border-rose-200",           Icon: XCircle,       label: "Invalide / Expirée" },
  missing: { cls: "bg-rose-100 text-rose-700 border-rose-200",           Icon: FileWarning,   label: "Non fourni" },
};

function DocPill({ item }) {
  const meta = STATUS_PILL[item.status] || STATUS_PILL.missing;
  const Icon = meta.Icon;
  const tone = item.status === "green" ? "emerald" : "rose"; // rule: green if valid, red otherwise
  const dotCls = tone === "emerald" ? "bg-emerald-500" : "bg-rose-500";
  return (
    <span
      data-testid={`td-pill-${item.doc_type}`}
      title={`${item.label} — ${meta.label}${item.valid_until ? ` (validité ${item.valid_until})` : ""}`}
      className={`inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-[11px] font-medium ${meta.cls}`}
    >
      <span className={`h-1.5 w-1.5 rounded-full ${dotCls}`} />
      <Icon className="h-3 w-3" />
      <span className="max-w-[130px] truncate">{item.label}</span>
    </span>
  );
}

function TechDocsOverview() {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    api.get("/tech-docs/overview").then((r) => setRows(r.data)).catch(() => setRows([]));
  }, []);

  if (rows === null) return (
    <Card className="mt-8 p-6 border-slate-200"><div className="text-sm text-slate-500">Chargement de la vue documents…</div></Card>
  );
  const withAssign = rows.filter((r) => (r.assigned_count || 0) > 0);
  const totals = rows.reduce((acc, r) => {
    for (const d of r.documents) {
      if (d.status === "green") acc.valid++;
      else acc.invalid++;
    }
    return acc;
  }, { valid: 0, invalid: 0 });

  return (
    <Card className="mt-8 p-6 border-slate-200" data-testid="rh-techdocs-overview">
      <div className="flex items-center justify-between mb-4 gap-4 flex-wrap">
        <div>
          <h2 className="font-heading text-xl font-semibold text-slate-900 flex items-center gap-2">
            <BadgeCheck className="h-5 w-5 text-[#0066FF]" />
            Documents professionnels — Vue d'ensemble
          </h2>
          <p className="text-sm text-slate-500 mt-1">
            État de validité des documents attribués à chaque employé. Vert = à jour, rouge = expiré, inexistant ou proche de l'expiration.
          </p>
        </div>
        <div className="flex items-center gap-3 text-xs">
          <span className="inline-flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-emerald-500" /> Valides : <b className="text-slate-900">{totals.valid}</b></span>
          <span className="inline-flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-rose-500" /> À traiter : <b className="text-slate-900">{totals.invalid}</b></span>
        </div>
      </div>

      {withAssign.length === 0 ? (
        <div className="text-sm text-slate-500 py-8 text-center border border-dashed border-slate-200 rounded-lg">
          Aucun employé n'a de documents professionnels attribués. Rendez-vous dans <Link to="/rh/employes" className="text-[#0066FF] underline">Employés</Link> pour en attribuer.
        </div>
      ) : (
        <>
        <div className="hidden md:block overflow-x-auto -mx-6 px-6 scroll-hint">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wider text-slate-500">
                <th className="py-2 pr-4 font-semibold">Employé</th>
                <th className="py-2 pr-4 font-semibold">Poste</th>
                <th className="py-2 pr-4 font-semibold">Documents</th>
                <th className="py-2 pr-2 font-semibold text-right">Action</th>
              </tr>
            </thead>
            <tbody>
              {withAssign.map((r) => (
                <tr key={r.user_id} className="border-t border-slate-100 align-top" data-testid={`td-row-${r.user_id}`}>
                  <td className="py-3 pr-4">
                    <div className="font-medium text-slate-900">{r.full_name}</div>
                    <div className="text-xs text-slate-500">{r.email}</div>
                  </td>
                  <td className="py-3 pr-4 text-slate-600">{r.poste || "—"}</td>
                  <td className="py-3 pr-4">
                    <div className="flex flex-wrap gap-1.5">
                      {r.documents.map((d) => <DocPill key={d.doc_type} item={d} />)}
                    </div>
                  </td>
                  <td className="py-3 pr-2 text-right">
                    <Link
                      to={`/rh/documents-pro/${r.user_id}`}
                      data-testid={`td-open-${r.user_id}`}
                      className="text-[#0066FF] text-xs font-medium hover:underline"
                    >
                      Ouvrir →
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="md:hidden space-y-3">
          {withAssign.map((r) => (
            <div key={r.user_id} className="rounded-lg border border-slate-200 p-3" data-testid={`td-card-${r.user_id}`}>
              <div className="flex items-start justify-between gap-2 mb-2">
                <div className="min-w-0">
                  <div className="font-medium text-slate-900 truncate">{r.full_name}</div>
                  <div className="text-xs text-slate-500 truncate">{r.poste || r.email}</div>
                </div>
                <Link
                  to={`/rh/documents-pro/${r.user_id}`}
                  data-testid={`td-open-m-${r.user_id}`}
                  className="text-[#0066FF] text-xs font-medium shrink-0"
                >
                  Ouvrir →
                </Link>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {r.documents.map((d) => <DocPill key={d.doc_type} item={d} />)}
              </div>
            </div>
          ))}
        </div>
        </>
      )}
    </Card>
  );
}

export default function RHDashboard() {
  const [stats, setStats] = useState(null);
  useEffect(() => { api.get("/stats/rh").then((r) => setStats(r.data)).catch(() => {}); }, []);

  return (
    <div>
      <PageHeader title="Tableau de bord RH" subtitle="Vue d'ensemble des demandes et de l'activité." />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-5">
        <Stat testId="rh-stat-employes" to="/rh/employes" icon={Users} label="Employés" value={stats?.total_employees ?? "—"} color="blue" />
        <Stat testId="rh-stat-conges" to="/rh/conges" icon={CalendarDays} label="Congés en attente" value={stats?.pending_leaves ?? "—"} color="amber" />
        <Stat testId="rh-stat-attestations" to="/rh/attestations" icon={FileText} label="Attestations en attente" value={stats?.pending_certs ?? "—"} color="emerald" />
        <Stat testId="rh-stat-bulletins" to="/rh/bulletins" icon={Receipt} label="Bulletins uploadés" value={stats?.total_payslips ?? "—"} color="slate" />
      </div>

      <TechDocsOverview />

      <Card className="mt-8 p-6 border-slate-200">
        <h2 className="font-heading text-xl font-semibold text-slate-900">Bienvenue sur l'espace RH</h2>
        <p className="text-sm text-slate-600 mt-2 leading-relaxed max-w-3xl">
          Depuis ce tableau de bord, vous pouvez gérer l'ensemble des demandes émises par vos employés :
          approuver ou rejeter les congés, valider les attestations de travail, et déposer les bulletins de paie mensuels.
          Chaque validation génère automatiquement un document PDF officiel FIO TELECOM.
        </p>
      </Card>
    </div>
  );
}
